import React, { useState, useEffect } from 'react';
import { Button, Input } from '../ui';
import InscricaoService from '../../services/Inscricoes/InscricaoService';
import AuthService from '../../services/AuthService';
import { Shield, AlertTriangle, CreditCard, User, Info, Check, Loader2, ArrowRight, Wallet } from 'lucide-react';

const inscricaoService = new InscricaoService();
const authService = new AuthService();

function formatCardNumber(value) {
  const v = value.replace(/\D/g, '').substring(0, 19);
  return v.replace(/(\d{4})(?=\d)/g, '$1.').trim();
}

function formatExpiry(value) {
  const v = value.replace(/\D/g, '').substring(0, 6);
  if (v.length > 2) {
    return `${v.substring(0, 2)}/${v.substring(2)}`;
  }
  return v;
}

function formatCPF(value) {
  const v = value.replace(/\D/g, '').substring(0, 14);
  if (v.length <= 11) {
    return v
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return v
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

function formatCEP(value) {
  const v = value.replace(/\D/g, '').substring(0, 8);
  if (v.length > 5) {
    return `${v.substring(0, 5)}-${v.substring(5)}`;
  }
  return v;
}

function formatPhone(value) {
  const v = value.replace(/\D/g, '').substring(0, 11);
  if (v.length > 6) {
    return `(${v.substring(0, 2)}) ${v.substring(2, 7)}-${v.substring(7)}`;
  }
  if (v.length > 2) {
    return `(${v.substring(0, 2)}) ${v.substring(2)}`;
  }
  return v;
}

function getDeviceInfo() {
  const screen = typeof window !== 'undefined' ? window.screen : null;
  return {
    colorDepth: screen ? (screen.colorDepth || 24) : 24,
    screenHeight: screen ? (screen.height || 900) : 900,
    screenWidth: screen ? (screen.width || 1440) : 1440,
    timeZoneOffset: new Date().getTimezoneOffset() / 60,
    language: (typeof navigator !== 'undefined' && navigator.language) ? navigator.language : 'pt-BR',
    userAgent: (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : ''
  };
}

export function CartaoForm({ inscricaoId, valorTotal, onSuccess, onError }) {
  const [loading, setLoading] = useState(false);
  const [loadingFatura, setLoadingFatura] = useState(false);
  const [erros, setErros] = useState([]);
  const [isCardDeclined, setIsCardDeclined] = useState(false);
  const [isExpired, setIsExpired] = useState(false);

  // Campos do Cartão (Asaas)
  const [nomeCartao, setNomeCartao] = useState('');
  const [numeroCartao, setNumeroCartao] = useState('');
  const [validade, setValidade] = useState(''); // MM/AAAA
  const [cvv, setCvv] = useState('');

  // Campos do Titular (Antifraude Asaas)
  const [cpf, setCpf] = useState('');
  const [emailTitular, setEmailTitular] = useState('');
  const [cep, setCep] = useState('');
  const [numeroEndereco, setNumeroEndereco] = useState('');
  const [telefone, setTelefone] = useState('');

  useEffect(() => {
    const carregarDadosUsuario = async () => {
      try {
        const userId = sessionStorage.getItem('brv-user-id');
        if (!userId) return;

        const response = await authService.getUserData(userId);
        if (response.ok) {
          const data = await response.json();
          if (data) {
            if (data.cpf) setCpf(formatCPF(data.cpf));
            if (data.telefone1) setTelefone(formatPhone(data.telefone1));
            if (data.cep) setCep(formatCEP(data.cep));
            if (data.numero) setNumeroEndereco(data.numero);
            if (data.first_name) {
              const nomeCompleto = `${data.first_name} ${data.last_name || ''}`.trim();
              setNomeCartao(nomeCompleto);
            }
          }
        }
      } catch (err) {
        console.error('Erro ao carregar dados do usuário para checkout:', err);
      }
    };

    carregarDadosUsuario();
  }, []);

  const validarCamposAsaas = () => {
    const novosErros = [];
    const numLimpo = numeroCartao.replace(/\D/g, '');
    const cpfLimpo = cpf.replace(/\D/g, '');
    const cepLimpo = cep.replace(/\D/g, '');
    const cvvLimpo = cvv.replace(/\D/g, '');
    const validadeLimpa = validade.replace(/\D/g, '');
    const telLimpo = telefone.replace(/\D/g, '');

    if (!nomeCartao.trim()) {
      novosErros.push('Nome impresso no cartão é obrigatório.');
    }
    if (!numLimpo || numLimpo.length < 13 || numLimpo.length > 19) {
      novosErros.push('Número de cartão inválido.');
    }
    if (!validadeLimpa || validadeLimpa.length < 6) {
      novosErros.push('Validade do cartão deve estar no formato MM/AAAA.');
    } else {
      const mes = parseInt(validadeLimpa.substring(0, 2), 10);
      if (mes < 1 || mes > 12) {
        novosErros.push('Mês de validade inválido (01 a 12).');
      }
    }
    if (!cvvLimpo || cvvLimpo.length < 3 || cvvLimpo.length > 4) {
      novosErros.push('Código CVV inválido (3 ou 4 dígitos).');
    }
    if (!cpfLimpo || (cpfLimpo.length !== 11 && cpfLimpo.length !== 14)) {
      novosErros.push('CPF ou CNPJ do titular do cartão é obrigatório.');
    }
    if (!emailTitular.trim()) {
      novosErros.push('E-mail do titular do cartão é obrigatório.');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTitular.trim())) {
      novosErros.push('E-mail do titular possui formato inválido.');
    }
    if (!telLimpo || telLimpo.length < 10) {
      novosErros.push('Celular do titular com DDD é obrigatório.');
    }
    if (!cepLimpo || cepLimpo.length !== 8) {
      novosErros.push('CEP de cobrança do titular é obrigatório (8 dígitos).');
    }
    if (!numeroEndereco.trim()) {
      novosErros.push('Número do endereço é obrigatório.');
    }

    return novosErros;
  };

  const handlePagarComAsaas = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    setErros([]);
    setIsCardDeclined(false);
    const errosValidacao = validarCamposAsaas();
    if (errosValidacao.length > 0) {
      setErros(errosValidacao);
      return;
    }

    setLoading(true);

    try {
      const validadeLimpa = validade.replace(/\D/g, '');
      const expiryMonth = validadeLimpa.substring(0, 2);
      const expiryYear = validadeLimpa.substring(2, 6);

      const callbackUrl = `${window.location.origin}/pagamento/sucesso?id_requisicao=${inscricaoId}&payment_type=credit_card&status=approved&origem=asaas_3ds`;

      const payload = {
        id_requisicao: inscricaoId,
        holder_name: nomeCartao.trim(),
        card_number: numeroCartao.replace(/\D/g, ''),
        expiry_month: expiryMonth,
        expiry_year: expiryYear,
        ccv: cvv.replace(/\D/g, ''),
        cpf_cnpj: cpf.replace(/\D/g, ''),
        email: emailTitular.trim(),
        postal_code: cep.replace(/\D/g, ''),
        address_number: numeroEndereco.trim(),
        phone: telefone ? telefone.replace(/\D/g, '') : '',
        device_info: getDeviceInfo(),
        callback_url: callbackUrl
      };

      const response = await inscricaoService.checkoutCartao(payload);
      const data = await response.json();

      if (!response.ok) {
        let errosList = [];
        let declined = false;
        const errorSource = data.erros || data.errors;

        if (errorSource && Array.isArray(errorSource)) {
          errosList = errorSource.map(item => {
            if (typeof item === 'object' && item !== null) {
              if (item.code === 'invalid_creditCard') declined = true;
              return item.description || item.message || item.mensagem || JSON.stringify(item);
            }
            return String(item);
          });
        } else if (data.detail) {
          if (String(data.detail).includes('invalid_creditCard') || String(data.detail).includes('não autorizada')) {
            declined = true;
          }
          errosList = Array.isArray(data.detail) ? data.detail : [data.detail];
        } else if (data.mensagem) {
          errosList = Array.isArray(data.mensagem) ? data.mensagem : [data.mensagem];
        } else if (data.message) {
          errosList = Array.isArray(data.message) ? data.message : [data.message];
        } else if (data.erro) {
          errosList = Array.isArray(data.erro) ? data.erro : [data.erro];
        } else if (data.error) {
          errosList = Array.isArray(data.error) ? data.error : [data.error];
        } else if (typeof data === 'object' && Object.keys(data).length > 0) {
          const possibleErros = [];
          Object.values(data).forEach(val => {
            if (Array.isArray(val)) {
              possibleErros.push(...val);
            } else if (typeof val === 'string') {
              possibleErros.push(val);
            }
          });
          errosList = possibleErros.length > 0 ? possibleErros : ['Ocorreu um erro ao processar o pagamento com cartão.'];
        } else {
          errosList = ['Ocorreu um erro ao processar o pagamento com cartão.'];
        }

        // Clean up empty strings or objects to prevent empty error bubbles
        errosList = errosList.map(e => typeof e === 'string' ? e : JSON.stringify(e))
            .filter(e => e && e.trim() !== '' && e !== '[]' && e !== '{}');

        if (errosList.length === 0) {
          errosList = ['Não foi possível concluir o pagamento. Verifique os dados e tente novamente.'];
        }

        const isExp = errosList.some(msg =>
          typeof msg === 'string' && (msg.toLowerCase().includes('expirad') || msg.toLowerCase().includes('prazo'))
        ) || (response.status === 400 && JSON.stringify(data).toLowerCase().includes('expirad'));

        if (isExp) {
          setIsExpired(true);
        }

        if (declined || JSON.stringify(data).includes('invalid_creditCard')) {
          setIsCardDeclined(true);
        }

        setErros(errosList);
        onError?.({ status: response.status, data, message: errosList.join(', ') });
      } else {
        const challengeUrl = data.three_d_secure_challenge_url || data.threeDSecureChallengeUrl;
        if (challengeUrl) {
          if (inscricaoId) {
            sessionStorage.setItem('brv-id-inscricao', inscricaoId);
          }
          window.location.href = challengeUrl;
          return;
        }

        const resultadoFormatado = {
          ...data,
          status: data.status || 'CONFIRMED',
          status_detail: (data.status === 'CONFIRMED' || data.status === 'RECEIVED') ? 'accredited' : (data.status === 'PENDING' ? 'pending' : data.status),
          order_id: data.order_id || data.id || inscricaoId
        };
        onSuccess?.(resultadoFormatado);
      }
    } catch (err) {
      console.error('Erro ao processar pagamento com cartão via Asaas:', err);
      setErros(['Erro de conexão. Tente novamente.']);
      onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePagarComFaturaAsaas = async () => {
    setLoadingFatura(true);
    setErros([]);
    try {
      const payload = { id_requisicao: inscricaoId };
      const response = await inscricaoService.checkoutCartao(payload);
      const data = await response.json();

      if (!response.ok) {
        let msg = data.detail || (data.erros && data.erros[0]?.description) || 'Falha ao gerar fatura segura do Asaas.';
        setErros([msg]);
      } else if (data.invoice_url) {
        window.location.href = data.invoice_url;
      } else {
        setErros(['Link da fatura não foi retornado pelo Asaas.']);
      }
    } catch (err) {
      console.error('Erro ao gerar fatura Asaas:', err);
      setErros(['Erro de conexão ao gerar fatura.']);
    } finally {
      setLoadingFatura(false);
    }
  };

  if (isExpired) {
    return (
      <div className="flex flex-col items-center p-6 gap-4 text-center bg-urb-card border border-urb-border rounded-xl">
        <div className="w-full p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 font-semibold text-sm">
          {erros[0] || 'O prazo para pagamento desta inscrição expirou.'}
        </div>
        <p className="text-sm text-slate-400 m-0">
          Como o prazo expirou, sua vaga foi liberada. Faça uma nova inscrição para participar do evento.
        </p>
        <a
          href="/novas-corridas"
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold bg-urb-primary text-black hover:bg-urb-primary/90 transition-colors"
        >
          <span>Fazer Nova Inscrição</span>
          <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    );
  }

  const formattedValor = typeof valorTotal === 'number'
    ? valorTotal.toFixed(2)
    : parseFloat(valorTotal || 0).toFixed(2);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-3 p-3.5 rounded-xl text-xs sm:text-sm bg-blue-500/10 border border-blue-500/30 text-blue-300">
        <Shield className="w-6 h-6 shrink-0 text-blue-400" />
        <span>
          <strong className="text-white">Ambiente Seguro:</strong> Seus dados de pagamento são criptografados e transmitidos com segurança diretamente para processamento bancário via Asaas.
        </span>
      </div>

      {valorTotal && (
        <div className="text-center py-1">
          <span className="text-xs text-slate-400">Valor da Inscrição: </span>
          <span className="font-extrabold text-lg text-white">R$ {formattedValor}</span>
        </div>
      )}

      {/* Alerta de recusa e opções de contingência */}
      {isCardDeclined && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/40 text-left">
          <div className="flex items-center gap-2 mb-2 text-red-400 font-bold text-sm">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <span>Transação não autorizada pelo banco emissor</span>
          </div>
          <div className="text-xs text-slate-300 space-y-1 mb-3">
            <p className="font-semibold text-slate-200">Dicas para aprovação rápida:</p>
            <ul className="list-disc pl-4 space-y-1">
              <li><strong>Titular diferente:</strong> Se o cartão for de outra pessoa, preencha o <strong>Nome e CPF do titular</strong> nos campos abaixo.</li>
              <li><strong>Cartão Virtual:</strong> Verifique no aplicativo do banco se o código CVV temporário ainda é válido.</li>
              <li><strong>Função Crédito:</strong> Certifique-se de que a função crédito e compras pela internet estão desbloqueadas.</li>
            </ul>
          </div>
          <Button
            type="button"
            variant="secondary"
            onClick={handlePagarComFaturaAsaas}
            disabled={loadingFatura}
            className="w-full text-xs font-semibold flex items-center justify-center gap-2 border-blue-500/40 text-blue-400 hover:bg-blue-500/10"
          >
            <Wallet className="w-4 h-4" />
            {loadingFatura ? "Abrindo fatura..." : "Pagar pela Fatura Segura do Asaas"}
          </Button>
        </div>
      )}

      {erros.length > 0 && !isCardDeclined && (
        <div className="p-3.5 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-xs sm:text-sm space-y-1">
          {erros.map((e, i) => (
            <p key={i} className="m-0">• {e}</p>
          ))}
        </div>
      )}

      <form onSubmit={handlePagarComAsaas} className="flex flex-col gap-5">
        {/* Seção 1: Dados do Cartão */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 pb-2 border-b border-urb-border text-sm font-bold text-white">
            <CreditCard className="w-4 h-4 text-urb-primary" />
            <span>Dados do Cartão de Crédito</span>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-400 mb-1 block">Número do Cartão *</label>
            <Input
              id="numeroCartao"
              value={numeroCartao}
              onChange={(e) => setNumeroCartao(formatCardNumber(e.target.value))}
              placeholder="0000.0000.0000.0000"
              maxLength={23}
              disabled={loading}
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-400 mb-1 block">Nome Impresso no Cartão *</label>
            <Input
              id="nomeCartao"
              value={nomeCartao}
              onChange={(e) => setNomeCartao(e.target.value.toUpperCase())}
              placeholder="Exatamente como gravado no cartão"
              className="uppercase"
              disabled={loading}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-400 mb-1 block">Validade (MM/AAAA) *</label>
              <Input
                id="validade"
                value={validade}
                onChange={(e) => setValidade(formatExpiry(e.target.value))}
                placeholder="MM/AAAA"
                maxLength={7}
                disabled={loading}
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-400 mb-1 block">CVV *</label>
              <Input
                id="cvv"
                value={cvv}
                maxLength={4}
                onChange={(e) => setCvv(e.target.value.replace(/\D/g, ''))}
                placeholder="Ex: 123"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Seção 2: Dados do Titular (Antifraude) */}
        <div className="space-y-3 pt-2">
          <div className="flex items-center gap-2 pb-2 border-b border-urb-border text-sm font-bold text-white">
            <User className="w-4 h-4 text-urb-primary" />
            <span>Dados do Titular do Cartão</span>
          </div>

          <div className="p-2.5 rounded-lg text-xs flex items-center gap-2 bg-slate-900 border border-slate-800 text-slate-400">
            <Info className="w-4 h-4 text-urb-primary shrink-0" />
            <span>Caso esteja usando cartão de terceiros, informe o <strong>Nome e CPF do titular</strong>.</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-400 mb-1 block">CPF ou CNPJ do Titular *</label>
              <Input
                id="cpf"
                value={cpf}
                onChange={(e) => setCpf(formatCPF(e.target.value))}
                placeholder="000.000.000-00"
                disabled={loading}
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-400 mb-1 block">E-mail do Titular *</label>
              <Input
                id="emailTitular"
                type="email"
                value={emailTitular}
                onChange={(e) => setEmailTitular(e.target.value)}
                placeholder="email@exemplo.com"
                disabled={loading}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-400 mb-1 block">Celular do Titular *</label>
              <Input
                id="telefone"
                value={telefone}
                onChange={(e) => setTelefone(formatPhone(e.target.value))}
                placeholder="(00) 00000-0000"
                disabled={loading}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-semibold text-slate-400 mb-1 block">CEP da Fatura *</label>
              <Input
                id="cep"
                value={cep}
                onChange={(e) => setCep(formatCEP(e.target.value))}
                placeholder="00000-000"
                maxLength={9}
                disabled={loading}
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-400 mb-1 block">Número *</label>
              <Input
                id="numeroEndereco"
                value={numeroEndereco}
                onChange={(e) => setNumeroEndereco(e.target.value)}
                placeholder="Ex: 450"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full py-3.5 font-bold text-base shadow-neon flex items-center justify-center gap-2 mt-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Processando pagamento...
            </>
          ) : (
            <>
              <Check className="w-5 h-5" />
              Confirmar Pagamento com Cartão
            </>
          )}
        </Button>

        <div className="text-center">
          <button
            type="button"
            onClick={handlePagarComFaturaAsaas}
            disabled={loading || loadingFatura}
            className="text-xs text-blue-400 hover:text-blue-300 underline font-medium transition-colors"
          >
            Prefere pagar com a Fatura Segura do Asaas?
          </button>
        </div>
      </form>
    </div>
  );
}

export default CartaoForm;
